import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  DeleteConfirmation,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS } from '../../common/constants';
import { initialTranslationsState } from '../../common/types/translations.types';
import {
  deleteMunicipalityAsync,
  getMunicipalityDetailsAsync,
  MunicipalityDetails,
  saveMunicipalityAsync,
  selectMunicipalityDetails,
  selectMunicipalityDetailsLoadingStatus,
  updateMunicipalityAsync,
} from './municipalitiesSlice';
import { getRegionsAsync, selectRegionItems } from '../regions/regionsSlice';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';
import { ensureTranslationItems } from '../../common/utils/translationUtils';

function MunicipalityAddOrUpdate() {
  const backLinkUrl = '/locations/municipalities';
  const params = useParams();
  const dispatch = useAppDispatch();
  const municipalityDetails = useAppSelector(selectMunicipalityDetails);
  const regions = useAppSelector(selectRegionItems);
  const municipalityDetailsLoadingStatus = useAppSelector(
    selectMunicipalityDetailsLoadingStatus,
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const isAddMode = !params.municipalityId;

  const initialMunicipalityState: MunicipalityDetails = {
    nameTranslations: initialTranslationsState,
    descriptionTranslations: initialTranslationsState,
    metaTitleTranslations: ensureTranslationItems([], 'MunicipalityMetaTitle'),
    metaDescriptionTranslations: ensureTranslationItems(
      [],
      'MunicipalityMetaDescription',
    ),
    latitude: '',
    longitude: '',
    regionId: '',
  };

  const [municipality, setMunicipality] = useState(initialMunicipalityState);
  const [municipalityNameFi, setMunicipalityNameFi] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [deleted, setDeleted] = useState(false);

  const regionOptions = regions.map(region => {
    return { value: region.id, label: region.name };
  });

  const initFetch = useCallback(() => {
    dispatch(getMunicipalityDetailsAsync(params.municipalityId));
    dispatch(
      getRegionsAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
      }),
    );
  }, [dispatch, params.municipalityId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (!isAddMode) {
      initFetch();
    }
  }, [isAddMode, initFetch]);

  useEffect(() => {
    if (!isAddMode) {
      setMunicipality({
        ...municipalityDetails,
        metaTitleTranslations: ensureTranslationItems(
          municipalityDetails.metaTitleTranslations,
          'MunicipalityMetaTitle',
        ),
        metaDescriptionTranslations: ensureTranslationItems(
          municipalityDetails.metaDescriptionTranslations,
          'MunicipalityMetaDescription',
        ),
      });
    }
  }, [isAddMode, municipalityDetails]);

  useEffect(() => {
    if (municipality.nameTranslations.length > 0) {
      const municipalityObject = municipality.nameTranslations.find(
        obj => obj.locale === 'fi_FI',
      );
      setMunicipalityNameFi(municipalityObject.text);
    }
  }, [municipality]);

  const deleteMunicipality = () => {
    dispatch(deleteMunicipalityAsync(params.municipalityId));
    setDeleted(true);
    setShowDeleteModal(false);
  };

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newMunicipality = { ...municipality };
    let pointer = newMunicipality;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setMunicipality(newMunicipality);
  };

  const handleTranslation = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { culture } = event.target.dataset;
    const textValue = event.target.value;
    const newMunicipality = { ...municipality };

    newMunicipality[finalProp] = municipality[finalProp].map(item => {
      if (item.locale === culture) {
        return { ...item, text: textValue };
      }
      return item;
    });
    setMunicipality(newMunicipality);
  };

  const handleSelectChange = (selectedOption, name) => {
    const newMunicipality = { ...municipality };
    newMunicipality[name] = selectedOption.value;
    setMunicipality(newMunicipality);
  };

  const addOrUpdateMunicipality = async () => {
    let actionResult: Action<string>;
    if (isAddMode) {
      actionResult = await dispatch(saveMunicipalityAsync(municipality));
    } else {
      actionResult = await dispatch(updateMunicipalityAsync(municipality));
    }

    if (
      saveMunicipalityAsync.fulfilled.match(actionResult) ||
      updateMunicipalityAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Kunnan ${municipalityNameFi} tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const generatePageTitle = function () {
    if (isAddMode) {
      return 'Uuden kunnan lisäys';
    }
    if (
      municipalityDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      municipalityNameFi !== ''
    ) {
      return `Kunnan tietojen päivitys: ${municipalityNameFi}`;
    }
    return 'Kunnan tietojen päivitys';
  };

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Kunnat</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Sijaintitiedot', 'Kunnat']}
          Title={generatePageTitle()}
        />

        <Loader
          IsLoading={
            municipalityDetailsLoadingStatus === LOADING_STATUS.LOADING
          }
        />

        {municipalityDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            {municipality.nameTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="nameTranslations"
                className="mb-40"
              >
                <Form.Label>Nimi ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="nameTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Form.Group className="mb-40">
              <Form.Label>Alue</Form.Label>
              <Select
                inputId="regionId"
                name="regionId"
                placeholder="Valitse alue"
                isSearchable
                options={regionOptions}
                onChange={option => handleSelectChange(option, 'regionId')}
                value={regionOptions.filter(
                  option => option.value === municipality.regionId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <hr className="hr-light mb-32" />

            {municipality.descriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="descriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Kuvaus ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="descriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {municipality.metaTitleTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaTitleTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-otsikko ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaTitleTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {municipality.metaDescriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaDescriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-kuvausteksti ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaDescriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Row xs={2}>
              <Col>
                <Form.Group controlId="latitude">
                  <Form.Label>Leveysaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={municipality.latitude}
                    onChange={handleInputChange}
                    name="latitude"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="longitude">
                  <Form.Label>Pituusaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={municipality.longitude}
                    onChange={handleInputChange}
                    name="longitude"
                  />
                </Form.Group>
              </Col>
            </Row>

            <InputErrorsDisplay inputErrors={inputErrors} />

            <ActionButtons
              CancelButtonText={
                deleted || submitted ? 'Palaa takaisin' : 'Peruuta'
              }
              CancelButtonUrl={backLinkUrl}
              DeleteButtonText={isAddMode || deleted ? '' : 'Poista kunta'}
              HandleDelete={
                isAddMode || deleted ? null : () => setShowDeleteModal(true)
              }
              HandleSave={addOrUpdateMunicipality}
            />

            {submitted && <Alert variant="success">Tiedot tallennettu!</Alert>}
            {(municipality.isDeleted || deleted) && (
              <Alert variant="danger">Kunta on poistettu!</Alert>
            )}
          </Container>
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={deleteMunicipality}
        HideModal={() => setShowDeleteModal(false)}
        Id={municipality.id}
        Message={`Haluatko varmasti poistaa kunnan ${municipalityNameFi}?`}
        ShowModal={showDeleteModal}
      />
    </>
  );
}

export default MunicipalityAddOrUpdate;
